/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useSelector, type TypedUseSelectorHook } from 'react-redux';
import type { Reducer } from 'redux';
import type { IAPDigitalPass } from '../../../types/InteragencyPass';
import type { GetEligiblePassesResponse } from '../actions/digitalPass';
import {
    FETCH_DIGITAL_PASSES_ERROR,
    FETCH_DIGITAL_PASSES_LOADING,
    FETCH_DIGITAL_PASSES_SUCCESS,
} from '../constants';
import {
    INTERAGENCY_ACCESS_PASS,
    INTERAGENCY_ANNUAL_SENIOR_PASS,
    INTERAGENCY_LIFETIME_SENIOR_PASS,
} from '../constants/passes';

export type DigitalPassesState = {
    passes: IAPDigitalPass[];
    loading: boolean;
    error: string | null;
};

export const useDigitalPassesSelector: TypedUseSelectorHook<{
    digitalPasses: DigitalPassesState;
}> = useSelector;

const INITIALSTATE: DigitalPassesState = {
    passes: [],
    loading: false,
    error: null,
};

const digitalPassTypeToPassTypeMap = {
    access: INTERAGENCY_ACCESS_PASS,
    'senior-annual': INTERAGENCY_ANNUAL_SENIOR_PASS,
    'senior-lifetime': INTERAGENCY_LIFETIME_SENIOR_PASS,
};

const digitalPassesReducer: Reducer<
    DigitalPassesState,
    { type: string; payload: GetEligiblePassesResponse; error?: any }
> = (state = INITIALSTATE, action) => {
    const st = { ...state };
    switch (action.type) {
        case FETCH_DIGITAL_PASSES_SUCCESS:
            st.passes = [] as IAPDigitalPass[];

            // maps IAP passes to their camping names, exlcudes non camping passes to simplify downstream logic
            action.payload.forEach((pass) => {
                if (pass.pass_type in digitalPassTypeToPassTypeMap) {
                    st.passes.push({
                        ...pass,
                        pass_type:
                            digitalPassTypeToPassTypeMap[
                                pass.pass_type as keyof typeof digitalPassTypeToPassTypeMap
                            ],
                    });
                }
            });

            st.loading = false;
            return st;
        case FETCH_DIGITAL_PASSES_ERROR:
            st.loading = false;
            st.error =
                action.error || 'There is a problem in retrieving the digital passes';
            return st;
        case FETCH_DIGITAL_PASSES_LOADING:
            st.loading = true;
            return st;
        default:
            return st;
    }
};

export default digitalPassesReducer;
